/**
 * A PON port's SFP over time: transmit power and module temperature, sampled
 * every fifteen minutes by the platform (the chassis keeps no history). One
 * dialog per port, off the PON table's transmit-power cell.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { api } from '../../lib/api';
import { Button, Spinner } from '../../components/ui';
import { WizardDialog } from '../../components/WizardDialog';
import { clockLabel, timeTicks } from '../../lib/traffic-axes';

interface Sample {
  at: number;
  txPowerDbm: number | null;
  temperatureC: number | null;
}

const SPANS: Array<{ hours: number; label: string }> = [
  { hours: 24, label: '24 h' },
  { hours: 24 * 7, label: '7 days' },
  { hours: 24 * 30, label: '30 days' },
];

/** A plain line over time with a faint grid, min/max/last beside it. Pixels, not a scaled viewBox. */
function Trend({
  samples,
  pick,
  unit,
  digits,
  stroke,
}: {
  samples: Sample[];
  pick: (s: Sample) => number | null;
  unit: string;
  digits: number;
  stroke: string;
}) {
  const points = samples.flatMap((s) => {
    const v = pick(s);
    return v === null ? [] : [{ at: s.at, v }];
  });
  if (points.length === 0) return <p className="text-sm text-muted">No readings in this span.</p>;
  const W = 560;
  const H = 120;
  const PAD = { l: 44, r: 12, t: 8, b: 20 };
  const t0 = points[0]!.at;
  const t1 = points[points.length - 1]!.at;
  const values = points.map((p) => p.v);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = Math.max(hi - lo, 0.5);
  const vMin = lo - span * 0.15;
  const vMax = hi + span * 0.15;
  const x = (at: number) => PAD.l + ((at - t0) / Math.max(t1 - t0, 1)) * (W - PAD.l - PAD.r);
  const y = (v: number) => PAD.t + (1 - (v - vMin) / (vMax - vMin)) * (H - PAD.t - PAD.b);
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.at).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const ticks = timeTicks(t0, t1);
  const last = values[values.length - 1]!;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-3 text-xs text-muted">
        <span className="text-ink">
          last <span className="tabular-nums">{last.toFixed(digits)} {unit}</span>
        </span>
        <span className="tabular-nums">
          min {lo.toFixed(digits)} · max {hi.toFixed(digits)}
        </span>
        <span>{points.length} samples</span>
      </div>
      <div className="overflow-x-auto">
        <svg width={W} height={H} className="block" role="img" aria-label={`${unit} over time`}>
          {[vMin, (vMin + vMax) / 2, vMax].map((v) => (
            <g key={v}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} stroke="var(--line)" strokeWidth={1} />
              <text x={PAD.l - 6} y={y(v) + 3} textAnchor="end" fontSize="10" fill="var(--faint)">
                {v.toFixed(digits)}
              </text>
            </g>
          ))}
          {ticks.at.map((at) => (
            <text key={at} x={x(at)} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--faint)">
              {clockLabel(at, ticks.withSeconds)}
            </text>
          ))}
          <path d={d} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" />
          <circle cx={x(t1)} cy={y(last)} r={2.5} fill={stroke} />
        </svg>
      </div>
    </div>
  );
}

export function PonPortOpticsDialog({
  oltId,
  port,
  onClose,
}: {
  oltId: string;
  port: string;
  onClose: () => void;
}) {
  const [hours, setHours] = useState(24);
  const query = useQuery({
    queryKey: ['olt', oltId, 'pon-optics', port, hours],
    queryFn: () =>
      api.get<{ items: Sample[] }>(
        `/olts/${encodeURIComponent(oltId)}/pon-optics?port=${encodeURIComponent(port)}&hours=${hours}`,
      ),
  });
  const samples = query.data?.items ?? [];
  return (
    <WizardDialog
      title={
        <span className="flex items-center gap-2">
          <Activity className="size-4 text-muted" />
          PON port {port} — optical module
        </span>
      }
      subtitle="The SFP in the port, sampled every 15 minutes over SNMP; the OLT side of every subscriber on it."
      maxWidth="lg"
      onClose={onClose}
      footer={
        <Button type="button" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-4 px-1">
        <div className="flex gap-1">
          {SPANS.map((s) => (
            <Button
              key={s.hours}
              size="sm"
              variant={s.hours === hours ? 'primary' : 'ghost'}
              onClick={() => setHours(s.hours)}
            >
              {s.label}
            </Button>
          ))}
        </div>
        {query.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : (
          <>
            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-faint">Transmit power</h4>
              <Trend samples={samples} pick={(s) => s.txPowerDbm} unit="dBm" digits={2} stroke="var(--success)" />
            </section>
            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-faint">Module temperature</h4>
              <Trend samples={samples} pick={(s) => s.temperatureC} unit="°C" digits={1} stroke="var(--warn)" />
            </section>
          </>
        )}
      </div>
    </WizardDialog>
  );
}
